/**
 * 製品ナビアイテムのトグル機能を管理
 *
 * 仕様
 * - ist-js__projuctsNavItem: 親
 * - ist-js__projuctsNavItemButton: ボタン
 * - ist-js__projuctsNavItemContent: コンテンツ
 *
 * 使い方
 * <div class="ist-js__projuctsNavItem">
 *   <button class="ist-js__projuctsNavItemButton">開閉ボタン1</button>
 *   <div class="ist-js__projuctsNavItemContent">
 *     コンテンツ1
 *   </div>
 * </div>
 *
 * <!-- 製品テンプレート下層: ナビのツールが表示されている間ぼかす -->
 * <div class="ist-js__projuctsNavItemActionContent">
 *   <!-- 製品テンプレート下層: コンテンツ -->
 *   <div class="ist-content ist-content--type-b ist-margin--t80 ist-reset">
 *     コンテンツ1
 *   </div>
 * </div>
 */

/**
 * 1つのトグルを管理するクラス
 */
export class ProjuctsNavItemToggle {
  private container: Element;
  private button: HTMLElement | null;
  private content: HTMLElement | null;
  private actionContents: Element[];

  constructor(container: Element) {
    this.container = container;
    this.button = container.querySelector('.ist-js__projuctsNavItemButton');
    this.content = container.querySelector('.ist-js__projuctsNavItemContent');
    this.actionContents = Array.from(
      document.querySelectorAll('.ist-js__projuctsNavItemActionContent')
    );
    this.init();
  }

  private init(): void {
    if (!this.button || !this.content) return;

    // contentにIDがない場合は生成して設定
    if (!this.content.id) {
      this.content.id = `projucts-nav-content-${Math.random().toString(36).substr(2, 9)}`;
    }

    // ariaの初期設定
    this.button.setAttribute('aria-controls', this.content.id);
    this.button.setAttribute('aria-expanded', 'false');

    // ボタンクリックイベント
    this.button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.toggleContent();
    });

    // クリックアウトサイドの検知
    document.addEventListener('click', this.handleClickOutside.bind(this));
    document.addEventListener('focusin', this.handleFocusOut.bind(this));
  }

  private handleClickOutside(event: MouseEvent): void {
    const target = event.target as Node;
    if (this.isOpen() && !this.container.contains(target)) {
      this.close();
    }
  }

  private handleFocusOut(event: FocusEvent): void {
    const target = event.target as Node;
    if (
      this.isOpen() &&
      !this.container.contains(target) &&
      !this.actionContents.some((content) => content.contains(target))
    ) {
      this.close();
    }
  }

  private toggleContent(): void {
    if (!this.content || !this.button) return;

    const isOpen = this.isOpen();

    if (!isOpen) {
      this.open();
      // コンテンツ内の最初のフォーカス可能な要素にフォーカス
      setTimeout(() => {
        const focusableElements = this.content?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusableElements && focusableElements.length > 0) {
          (focusableElements[0] as HTMLElement).focus();
        }
      }, 10);
    } else {
      this.close();
    }
  }

  private toggleActionContentState(isOpen: boolean): void {
    if (!this.actionContents) return;

    if (isOpen) {
      this.actionContents.forEach((actionContent) => {
        actionContent.classList.add(
          'ist-js__projuctsNavItemActionContent--open'
        );
      });
    } else {
      this.actionContents.forEach((actionContent) => {
        actionContent.classList.remove(
          'ist-js__projuctsNavItemActionContent--open'
        );
      });
    }
  }

  public isOpen(): boolean {
    return (
      this.content?.classList.contains('ist-js__projuctsNavItemOpen') || false
    );
  }

  public open(): void {
    if (!this.content || !this.button) return;

    this.content.classList.add('ist-js__projuctsNavItemOpen');
    this.button.classList.add(
      'ist-js__projuctsNavItemButton--animate',
      'ist-js__projuctsNavItemButton--open'
    );
    this.button.setAttribute('aria-expanded', 'true');
    this.toggleActionContentState(true);
  }

  public close(): void {
    if (!this.content || !this.button) return;

    this.content.classList.remove('ist-js__projuctsNavItemOpen');
    this.button.classList.remove(
      'ist-js__projuctsNavItemButton--animate',
      'ist-js__projuctsNavItemButton--open'
    );
    this.button.setAttribute('aria-expanded', 'false');
    this.toggleActionContentState(false);
  }

  public destroy(): void {
    document.removeEventListener('click', this.handleClickOutside.bind(this));
    document.removeEventListener('focusin', this.handleFocusOut.bind(this));
  }
}

/**
 * 複数のトグルを管理するマネージャークラス
 */
export class ProjuctsNavItemToggleManager {
  private toggles: ProjuctsNavItemToggle[] = [];

  constructor() {
    this.init();
  }

  private init(): void {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.initializeToggles();
      });
    } else {
      this.initializeToggles();
    }
  }

  private initializeToggles(): void {
    const containers = document.querySelectorAll(
      '.ist-pc .ist-js__projuctsNavItem'
    );
    containers.forEach((container) => {
      this.toggles.push(new ProjuctsNavItemToggle(container));
    });
  }

  public getToggles(): ProjuctsNavItemToggle[] {
    return this.toggles;
  }

  public openAll(): void {
    this.toggles.forEach((toggle) => toggle.open());
  }

  public closeAll(): void {
    this.toggles.forEach((toggle) => toggle.close());
  }

  public getOpenToggles(): ProjuctsNavItemToggle[] {
    return this.toggles.filter((toggle) => toggle.isOpen());
  }

  public destroy(): void {
    this.toggles.forEach((toggle) => toggle.destroy());
    this.toggles = [];
  }
}
